import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Code } from "@connectrpc/connect";
import { useQuery } from "@connectrpc/connect-query";
import { Link, createFileRoute } from "@tanstack/react-router";

import { serverTimeAt } from "@yatayat/core/clock";
import { formatDate, formatNepalTime, nepalIsoDate, nepalToday } from "@yatayat/core/date";
import { formatNumber, type Lang } from "@yatayat/core/money";
import { prepareTicketVerifier } from "@yatayat/core/ticket";
import { BookingService } from "@yatayat/gen/yatayat/booking/v1/booking_pb";
import { OperatorService } from "@yatayat/gen/yatayat/operator/v1/operator_pb";

import { QrScanner, preloadQrDecoder } from "~/components/QrScanner";
import { Loading } from "~/components/QueryState";
import { DepartureForm, DepartureNotYet, DepartureRecorded, MarkDepartedButton, departureTooEarly } from "~/components/TripDeparture";
import {
  boardedCount,
  useBoarding,
  useOnline,
  type CachedManifest,
  type DepartureRefusal,
  type IssueOutcome,
  type ScanCheck,
  type ScanRecord,
  type SyncIssue,
  type SyncStatus,
  type TripBoardingState,
  type Verdict,
} from "~/lib/boarding";
import { useI18n, type MessageKey } from "~/lib/i18n";
import { localDigits } from "~/lib/labels";
import { useTripChanges } from "~/lib/live";
import { errorCode, retryTransient } from "~/lib/rpc/errors";
import { can, useStaffSession } from "~/lib/staff";
import { useRefreshStaffSession } from "~/lib/staff-refresh";
import { ui } from "~/lib/ui";

export const Route = createFileRoute("/operator/trips/$tripId/scan")({
  component: ScanPage,
});

function ScanPage() {
  const { tripId } = Route.useParams();
  const { t } = useI18n();
  const staff = useStaffSession();
  const session = staff.session;

  if (staff.isPending) return <Loading />;

  if (!session || !can.board(session.role)) {
    return (
      <div className="mx-auto max-w-xl space-y-3">
        <p className="text-slate-600 dark:text-slate-400">{t("scanNotAllowed")}</p>
        <Link to="/operator" className="text-sm text-ink underline underline-offset-2 dark:text-ink-dark">
          {t("scanToOperator")}
        </Link>
      </div>
    );
  }

  // Each trip keeps its own queue and cache.
  return <BoardingScan key={tripId} tripId={tripId} />;
}

/** Buzzes once for a pass and three times for anything else, so the result can be felt. */
function buzz(verdict: Verdict | "error") {
  if (!("vibrate" in navigator)) return;
  try {
    navigator.vibrate(verdict === "valid" ? 80 : [120, 80, 120]);
  } catch {
    // Some browsers refuse vibration without a recent tap; the card still shows.
  }
}

function BoardingScan({ tripId }: { tripId: string }) {
  const { t } = useI18n();
  const online = useOnline();
  const refreshStaffSession = useRefreshStaffSession();
  const { store, trip, keys, sync, persisted } = useBoarding(tripId);

  // Fetched while online and cached in the store, which is all scanning reads.
  const keysQuery = useQuery(BookingService.method.listTicketKeys, {}, { retry: retryTransient });
  // New sales, cancellations and other phones' boardings keep the offline list current;
  // polling covers a dropped live feed and pauses while offline.
  const manifestQuery = useQuery(OperatorService.method.getManifest, { tripId }, { retry: retryTransient, refetchInterval: 30_000 });
  const refetchManifest = manifestQuery.refetch;
  useTripChanges(tripId, () => void refetchManifest());

  useEffect(() => {
    if (keysQuery.data) store.saveKeys(keysQuery.data.keys, serverTimeAt(keysQuery.dataUpdatedAt));
  }, [keysQuery.data, keysQuery.dataUpdatedAt, store]);
  useEffect(() => {
    if (manifestQuery.data) store.saveManifest(manifestQuery.data, serverTimeAt(manifestQuery.dataUpdatedAt));
  }, [manifestQuery.data, manifestQuery.dataUpdatedAt, store]);

  // An ended staff session shows the sign-in; the queue waits in storage meanwhile.
  const manifestError = manifestQuery.isError ? errorCode(manifestQuery.error) : undefined;
  const signedOut = manifestError === Code.Unauthenticated || sync.state === "signed_out";
  useEffect(() => {
    if (signedOut) void refreshStaffSession();
  }, [signedOut, refreshStaffSession]);

  // Load the verifier and QR reader now, while the network is likely up.
  const [verifier, setVerifier] = useState<"loading" | "ready" | "unavailable">("loading");
  useEffect(() => {
    let live = true;
    prepareTicketVerifier().then(
      () => live && setVerifier("ready"),
      () => live && setVerifier("unavailable"),
    );
    // The scanner says so itself if the reader is missing later.
    preloadQrDecoder().catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  const [camera, setCamera] = useState(false);
  const [shown, setShown] = useState<string | "error" | null>(null);

  const check = useCallback(
    (payload: string) => {
      store.scan(payload).then(
        (rec) => {
          setShown(rec.scanId);
          buzz(rec.check.verdict);
        },
        () => {
          setShown("error");
          buzz("error");
        },
      );
    },
    [store],
  );

  if (manifestError === Code.NotFound || manifestError === Code.InvalidArgument || manifestError === Code.PermissionDenied) {
    return (
      <div className="mx-auto max-w-xl space-y-3">
        <BackToTrip tripId={tripId} />
        <p className="text-slate-600 dark:text-slate-400">{t("scanTripNotFound")}</p>
      </div>
    );
  }

  const shownRecord = shown && shown !== "error" ? trip.recent.find((r) => r.scanId === shown) : undefined;

  return (
    <div className="mx-auto max-w-xl space-y-5">
      <div className="space-y-2">
        <BackToTrip tripId={tripId} />
        <h1 className={ui.h1}>{t("scanTitle")}</h1>
        <TripLine manifest={trip.manifest} />
      </div>

      <Readiness online={online} trip={trip} keysSavedAtMs={keys?.savedAtMs ?? 0} />
      <SyncPanel online={online} trip={trip} sync={sync} onSync={() => void store.sync()} />

      {!persisted && (
        <p role="alert" className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-900/30 dark:text-amber-200">
          {t("scanNotSaved")}
        </p>
      )}
      {verifier === "unavailable" && (
        <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-950/40 dark:text-red-300">
          {t("scanNoVerifier")}
        </p>
      )}

      <section aria-label={t("scanTitle")} className="space-y-3">
        {camera && <QrScanner onScan={check} />}
        <button
          type="button"
          onClick={() => setCamera(!camera)}
          className={
            camera
              ? "w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-900"
              : "w-full rounded-xl bg-ink px-4 py-3 text-base font-semibold text-white dark:bg-ink-dark dark:text-slate-950"
          }
        >
          {t(camera ? "scanStopCamera" : "scanStartCamera")}
        </button>

        <div aria-live="assertive" aria-atomic="true">
          {shown === "error" && <ErrorCard />}
          {shownRecord && <ResultCard key={shownRecord.scanId} record={shownRecord} manifest={trip.manifest} />}
        </div>

        <PasteForm onSubmit={check} />
      </section>

      <Departure trip={trip} onDepart={(n) => store.depart(n)} />

      {trip.issues.length > 0 && <SyncIssues issues={trip.issues} />}
      <RecentScans recent={trip.recent} />
    </div>
  );
}

function BackToTrip({ tripId }: { tripId: string }) {
  const { t } = useI18n();

  return (
    <Link
      to="/operator/trips/$tripId"
      params={{ tripId }}
      className="text-sm text-ink underline-offset-2 hover:underline dark:text-ink-dark"
    >
      <span aria-hidden="true">← </span>
      {t("scanBackToTrip")}
    </Link>
  );
}

/** Formats a past moment as a Nepal clock time, with the date when it isn't today. */
function when(ms: number, lang: Lang): string {
  const time = formatNepalTime(ms, lang);
  return nepalIsoDate(ms) === nepalToday() ? time : `${formatDate(ms, lang)} · ${time}`;
}

function TripLine({ manifest }: { manifest: CachedManifest | null }) {
  const { lang } = useI18n();
  if (!manifest || manifest.departsAtMs === 0) return null;

  return (
    <p className="text-sm text-slate-600 dark:text-slate-400">
      {formatDate(manifest.departsAtMs, lang)} · {formatNepalTime(manifest.departsAtMs, lang)}
      {manifest.routeName?.[lang] && ` · ${manifest.routeName[lang]}`}
      {manifest.plateNo && ` · ${localDigits(manifest.plateNo, lang)}`}
    </p>
  );
}

function Readiness({ online, trip, keysSavedAtMs }: { online: boolean; trip: TripBoardingState; keysSavedAtMs: number }) {
  const { t, lang } = useI18n();
  const manifest = trip.manifest;
  const sold = manifest ? manifest.tickets.filter((tk) => !tk.cancelled).length : 0;

  let readiness: string;
  if (!keysSavedAtMs) {
    readiness = t("scanNotReady");
  } else if (!manifest) {
    readiness = t("scanKeysOnly", { time: when(keysSavedAtMs, lang) });
  } else {
    readiness = t(sold === 1 ? "scanReadyOne" : "scanReady", { n: formatNumber(sold, lang), time: when(keysSavedAtMs, lang) });
  }

  return (
    <div className="space-y-1.5 rounded-xl border border-slate-200 p-4 text-sm dark:border-slate-800">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
            online
              ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300"
              : "bg-slate-200 text-slate-800 dark:bg-slate-800 dark:text-slate-200"
          }`}
        >
          <span aria-hidden="true" className={`h-2 w-2 rounded-full ${online ? "bg-emerald-600 dark:bg-emerald-400" : "bg-slate-500"}`} />
          {t(online ? "scanOnline" : "scanOffline")}
        </span>
        <p className={keysSavedAtMs ? "font-medium" : "font-medium text-amber-800 dark:text-amber-300"}>{readiness}</p>
      </div>
      {manifest && <p className="text-slate-600 dark:text-slate-400">{t("scanListAt", { time: when(manifest.savedAtMs, lang) })}</p>}
    </div>
  );
}

function SyncPanel(props: {
  online: boolean;
  trip: TripBoardingState;
  sync: SyncStatus;
  onSync: () => void;
}) {
  const { t, lang } = useI18n();
  const pending = props.trip.queue.length;
  const state = props.sync.state;

  let note: MessageKey | null = null;
  if (state === "syncing") note = "scanSyncing";
  else if (state === "signed_out") note = "scanSyncSignedOut";
  else if (state === "failed") note = "scanSyncFailed";
  else if (pending > 0 && !props.online) note = "scanSyncWhenOnline";
  else if (state === "retrying") note = "scanSyncRetrying";

  const trouble = state === "signed_out" || state === "failed" || state === "retrying";

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-slate-200 p-4 text-sm dark:border-slate-800">
      <div className="mr-auto space-y-0.5">
        <p className="font-medium">
          {pending > 0 ? t("scanPending", { n: formatNumber(pending, lang) }) : t("scanAllSynced")}
          {props.trip.lastSyncAtMs > 0 && (
            <span className="font-normal text-slate-600 dark:text-slate-400"> · {t("scanLastSync", { time: when(props.trip.lastSyncAtMs, lang) })}</span>
          )}
        </p>
        {note && (
          <p role={trouble ? "alert" : "status"} className={trouble ? "text-amber-800 dark:text-amber-300" : "text-slate-600 dark:text-slate-400"}>
            {t(note)}
          </p>
        )}
      </div>
      {pending > 0 && props.online && state !== "syncing" && (
        <button
          type="button"
          onClick={props.onSync}
          className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-900"
        >
          {t("scanSyncNow")}
        </button>
      )}
    </div>
  );
}

const refusalLabels: Record<DepartureRefusal, MessageKey> = {
  too_few: "departureServerTooFew",
  closed: "departureRefused",
};

/**
 * Marks the trip departed from the scan page. Offline, the departure waits on this
 * phone and is sent after the scans; the server times it when it arrives.
 */
function Departure({ trip, onDepart }: { trip: TripBoardingState; onDepart: (headcount: number) => void }) {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const manifest = trip.manifest;
  const departure = trip.departure;

  let body: ReactNode;
  if (manifest && manifest.departedAtMs > 0) {
    body = <DepartureRecorded departedAtMs={manifest.departedAtMs} headcount={manifest.headcount} />;
  } else if (departure?.state === "recorded") {
    body = <DepartureRecorded departedAtMs={departure.departedAtMs} headcount={departure.headcount} />;
  } else if (manifest && departureTooEarly(manifest.departsAtMs)) {
    body = <DepartureNotYet departsAtMs={manifest.departsAtMs} />;
  } else if (open) {
    body = (
      <DepartureForm
        boarded={boardedCount(trip)}
        seatCount={manifest?.seatCount ?? 0}
        pending={false}
        error={null}
        onSubmit={(n) => {
          onDepart(n);
          setOpen(false);
        }}
        onCancel={() => setOpen(false)}
      />
    );
  } else {
    body = (
      <div className="space-y-2">
        {departure?.state === "waiting" && (
          <p role="status" className="text-sm text-slate-700 dark:text-slate-300">
            {t("departureSaved", { n: formatNumber(departure.headcount, lang) })}
          </p>
        )}
        {departure?.state === "refused" && (
          <p role="alert" className="text-sm text-brand dark:text-brand-dark">
            {t(refusalLabels[departure.reason])}
          </p>
        )}
        <MarkDepartedButton onClick={() => setOpen(true)} again={departure?.state === "waiting"} />
      </div>
    );
  }

  return (
    <section aria-labelledby="departure" className="space-y-2">
      <h2 id="departure" className="text-lg font-semibold">
        {t("departureTitle")}
      </h2>
      {body}
    </section>
  );
}

const verdictLabels: Record<Verdict, MessageKey> = {
  valid: "scanValid",
  duplicate: "scanDuplicate",
  wrong_trip: "scanWrongTrip",
  cancelled: "scanCancelled",
  invalid: "scanInvalid",
};

const cardTone: Record<Verdict, string> = {
  valid: "border-emerald-600 bg-emerald-50 text-emerald-950 dark:border-emerald-500 dark:bg-emerald-950/60 dark:text-emerald-50",
  duplicate: "border-amber-500 bg-amber-50 text-amber-950 dark:border-amber-400 dark:bg-amber-950/50 dark:text-amber-50",
  wrong_trip: "border-red-600 bg-red-50 text-red-950 dark:border-red-500 dark:bg-red-950/60 dark:text-red-50",
  cancelled: "border-red-600 bg-red-50 text-red-950 dark:border-red-500 dark:bg-red-950/60 dark:text-red-50",
  invalid: "border-red-600 bg-red-50 text-red-950 dark:border-red-500 dark:bg-red-950/60 dark:text-red-50",
};

const pillTone: Record<Verdict, string> = {
  valid: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300",
  duplicate: "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200",
  wrong_trip: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  cancelled: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  invalid: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
};

const verdictIcons: Record<Verdict, string> = { valid: "✓", duplicate: "!", wrong_trip: "✕", cancelled: "✕", invalid: "✕" };

const invalidReasons: Record<Extract<ScanCheck, { verdict: "invalid" }>["reason"], MessageKey> = {
  malformed: "scanMalformed",
  unknown_key: "scanUnknownKey",
  bad_signature: "scanBadSignature",
};

/** A place's name from the cached manifest, falling back to its code. */
function placeLabel(manifest: CachedManifest | null, code: string, lang: Lang): string {
  return manifest?.places[code]?.[lang] || code;
}

function ResultCard({ record, manifest }: { record: ScanRecord; manifest: CachedManifest | null }) {
  const { t, lang } = useI18n();
  const { check } = record;

  let note: string | null = null;
  switch (check.verdict) {
    case "valid":
      if (!check.inManifest && manifest) note = t("scanNotInList");
      break;
    case "duplicate":
      note = t(check.here ? "scanBoardedHere" : "scanBoardedBefore", { time: when(check.boardedAtMs, lang) });
      break;
    case "wrong_trip":
      note = t("scanWrongTripBody");
      break;
    case "cancelled":
      note = t("scanCancelledBody");
      break;
    case "invalid":
      note = t(invalidReasons[check.reason]);
  }

  return (
    <article className={`space-y-2 rounded-2xl border-4 p-5 text-center ${cardTone[check.verdict]}`}>
      <h2 className="text-4xl font-bold uppercase tracking-wide">
        <span aria-hidden="true">{verdictIcons[check.verdict]} </span>
        {t(verdictLabels[check.verdict])}
      </h2>
      {check.verdict !== "invalid" && (
        <>
          <p className="text-5xl font-bold">{t("seatNo", { no: localDigits(check.claims.seatNo, lang) })}</p>
          <p className="text-lg font-semibold">
            {placeLabel(manifest, check.claims.fromCode, lang)}
            <span aria-hidden="true"> → </span>
            <span className="sr-only"> – </span>
            {placeLabel(manifest, check.claims.toCode, lang)}
          </p>
          <p className="font-mono text-sm tracking-wider">{check.claims.code}</p>
        </>
      )}
      {note && <p className="text-sm font-medium">{note}</p>}
      {record.issue && <p className="text-sm font-semibold">{t(issueLabels[record.issue].label)}</p>}
    </article>
  );
}

function ErrorCard() {
  const { t } = useI18n();

  return (
    <article className={`space-y-2 rounded-2xl border-4 p-5 text-center ${cardTone.invalid}`}>
      <p className="text-sm font-medium">{t("scanNoVerifier")}</p>
    </article>
  );
}

function PasteForm({ onSubmit }: { onSubmit: (payload: string) => void }) {
  const { t } = useI18n();
  const [text, setText] = useState("");

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!text.trim()) return;
    onSubmit(text);
    setText("");
  }

  return (
    <form onSubmit={submit} className="space-y-1.5">
      <label htmlFor="qr-text" className="block text-sm font-medium">
        {t("scanPasteLabel")}
      </label>
      <div className="flex gap-2">
        <input
          id="qr-text"
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          aria-describedby="qr-text-hint"
          className="min-w-0 flex-1 rounded-md border border-slate-300 bg-white px-3 py-2 font-mono text-sm dark:border-slate-700 dark:bg-slate-950"
        />
        <button
          type="submit"
          className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-900"
        >
          {t("scanCheck")}
        </button>
      </div>
      <p id="qr-text-hint" className="text-xs text-slate-600 dark:text-slate-400">
        {t("scanPasteHint")}
      </p>
    </form>
  );
}

const issueLabels: Record<IssueOutcome, { label: MessageKey; withTime?: MessageKey }> = {
  already_boarded: { label: "scanIssueAlreadyBoarded", withTime: "scanIssueAlreadyBoardedAt" },
  cancelled: { label: "scanIssueCancelled" },
  wrong_trip: { label: "scanIssueWrongTrip" },
  invalid: { label: "scanIssueInvalid" },
};

function SyncIssues({ issues }: { issues: SyncIssue[] }) {
  const { t, lang } = useI18n();

  return (
    <section aria-labelledby="sync-issues" className="space-y-2 rounded-xl border border-amber-300 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/30">
      <h2 id="sync-issues" className="font-semibold text-amber-950 dark:text-amber-100">
        {t("scanIssues")}
      </h2>
      <p className="text-sm text-amber-900 dark:text-amber-200">{t("scanIssuesIntro")}</p>
      <ul className="space-y-1 text-sm">
        {issues.map((issue) => {
          const labels = issueLabels[issue.outcome];
          const text =
            labels.withTime && issue.boardedAtMs > 0 ? t(labels.withTime, { time: when(issue.boardedAtMs, lang) }) : t(labels.label);

          return (
            <li key={issue.scanId}>
              <span className="font-mono font-semibold">{issue.code}</span>
              {issue.seatNo && ` · ${t("seatNo", { no: localDigits(issue.seatNo, lang) })}`}
              {`: ${text}`}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function RecentScans({ recent }: { recent: ScanRecord[] }) {
  const { t, lang } = useI18n();

  return (
    <section aria-labelledby="recent-scans" className="space-y-2">
      <h2 id="recent-scans" className="text-lg font-semibold">
        {t("scanRecent")}
      </h2>
      {recent.length === 0 ? (
        <p className="text-sm text-slate-600 dark:text-slate-400">{t("scanNoRecent")}</p>
      ) : (
        <ol className="divide-y divide-slate-100 rounded-xl border border-slate-200 text-sm dark:divide-slate-800 dark:border-slate-800">
          {recent.map((r) => (
            <li key={r.scanId} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5">
              <time dateTime={new Date(r.scannedAtMs).toISOString()} className="tabular-nums text-slate-600 dark:text-slate-400">
                {formatNepalTime(r.scannedAtMs, lang)}
              </time>
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${pillTone[r.check.verdict]}`}>
                {t(verdictLabels[r.check.verdict])}
              </span>
              <span className="min-w-0 flex-1 truncate">
                {r.check.verdict === "invalid" ? (
                  t(invalidReasons[r.check.reason])
                ) : (
                  <>
                    {t("seatNo", { no: localDigits(r.check.claims.seatNo, lang) })}
                    {" · "}
                    <span className="font-mono">{r.check.claims.code}</span>
                  </>
                )}
              </span>
              <SyncMark record={r} />
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function SyncMark({ record }: { record: ScanRecord }): ReactNode {
  const { t } = useI18n();
  if (record.check.verdict !== "valid") return null;

  if (record.issue) {
    return <span className="text-xs font-medium text-red-700 dark:text-red-400">{t(issueLabels[record.issue].label)}</span>;
  }

  return record.synced ? (
    <span className="text-xs text-emerald-700 dark:text-emerald-400">
      <span aria-hidden="true">✓ </span>
      {t("scanSynced")}
    </span>
  ) : (
    <span className="text-xs text-slate-600 dark:text-slate-400">{t("scanWaiting")}</span>
  );
}
